import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Hls from 'hls.js';
import api, { getIdToken } from '../lib/api';
import { PageHead, Spinner, ErrorNote, useAsync } from '../components/ui';
import RichText from '../components/RichText';
import { cssBackground } from '../components/ColorField';
import { textOn } from '../lib/color';
import RichTextEditor from '../components/RichTextEditor';
import ProgramHero from '../components/ProgramHero';
import '../styles/learn.css';

const RATES = [1, 1.25, 1.5, 2, 0.75];
const fmt = (s) => {
  if (!s || isNaN(s)) return '0:00';
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
};

export default function LessonPlayerPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { loading, error, data, reload } = useAsync(() => api.getLearnLesson(id), [id]);
  const [override, setOverride] = useState(null);
  const [note, setNote] = useState('');
  const notesRef = useRef(null);

  // Local (device) note — server sync is a later feature.
  useEffect(() => { setNote(localStorage.getItem(`cp_note_${id}`) || ''); }, [id]);
  const saveNote = (v) => { setNote(v); localStorage.setItem(`cp_note_${id}`, v); };

  // Submissions (reflections + questions) the current learner has sent.
  const [subs, setSubs] = useState([]);
  const reloadSubs = () => api.learnMySubmissions(id).then((s) => setSubs(Array.isArray(s) ? s : [])).catch(() => {});
  useEffect(() => { reloadSubs(); /* eslint-disable-next-line */ }, [id]);
  // Opening the lesson = the instructor's replies here are seen (2026-10-15).
  useEffect(() => {
    api.lessonRepliesSeen(id).then(() => window.dispatchEvent(new Event('badge-refresh'))).catch(() => {});
  }, [id]);

  if (loading) return <div className="page"><Spinner /></div>;
  if (error) {
    if (error.status === 403) {
      return (
        <div className="page">
          <PageHead breadcrumb={<a onClick={() => navigate('/courses')} style={{ cursor: 'pointer' }}>Courses</a>} title="PRO lesson" />
          <div className="upsell">This lesson is part of a <b>PRO</b> program. Subscribe to unlock it. Web purchase is coming soon — for now, subscribe in the iOS or Android app.</div>
        </div>
      );
    }
    if (error.status === 423) {
      return (
        <div className="page">
          <PageHead breadcrumb={<a onClick={() => navigate('/courses')} style={{ cursor: 'pointer' }}>Courses</a>} title="Locked lesson" />
          <div className="upsell">🔒 This lesson unlocks once you’ve completed the earlier lessons in the course. Finish those first, then come back.</div>
        </div>
      );
    }
    return <div className="page"><ErrorNote error={error} onRetry={reload} /></div>;
  }

  const blocks = data.blocks || [];

  const isDone = override == null ? !!data.completed : override;
  const toggleComplete = async () => {
    const next = !isDone;
    setOverride(next);
    try { await api.saveLearnProgress(id, { completed: next, lastPositionSeconds: data.lastPositionSeconds || 0 }); }
    catch (e) { alert(e.message); setOverride(!next); }
  };
  const savePosition = (sec) => { api.saveLearnProgress(id, { completed: isDone, lastPositionSeconds: sec }).catch(() => {}); };
  const scrollToNotes = () => {
    notesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    // Put the cursor in the notes editor so the user can type their reflection
    // straight away (otherwise the button just scrolls and seems to do nothing).
    setTimeout(() => {
      const area = notesRef.current?.querySelector('.rte-area');
      if (area) {
        area.focus();
        // Place the caret at the end of any existing notes.
        const sel = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(area);
        range.collapse(false);
        sel?.removeAllRanges();
        sel?.addRange(range);
      }
    }, 350);
  };

  return (
    <div className="page">
      <PageHead breadcrumb={<a onClick={() => navigate(`/learn/course/${data.courseId}`)} style={{ cursor: 'pointer' }}>Course</a>} title="" />

      <LessonHero lesson={data} />

      <div className="section">
        {/* Blocks render top-to-bottom in the order the lesson was arranged. */}
        {blocks.length === 0
          ? <div style={{ color: 'var(--muted)' }}>This lesson has no content yet.</div>
          : blocks.map((b) => (
            <div key={b.id} style={{ marginBottom: 18 }}>
              <LessonBlockView block={b} lessonId={id} mySubs={subs} onSubmitted={reloadSubs}
                initialPosition={data.lastPositionSeconds} onSavePosition={savePosition} onAddNote={scrollToNotes} />
            </div>
          ))}

        <AskInstructor lessonId={id} mine={subs.filter((s) => s.kind === 'QUESTION')} onSubmitted={reloadSubs} />

        {data.askAi !== false && <AskThisLesson key={id} lessonId={id} />}

        <div ref={notesRef}>
          <PlayerPanel title="📝 Your notes">
            <RichTextEditor value={note} onChange={saveNote} minHeight={140} placeholder="Your private notes for this lesson…" />
            <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: 6 }}>Saved on this device. (Synced notes are a future feature.)</div>
          </PlayerPanel>
        </div>

        <div style={{ marginTop: 20, display: 'flex', gap: 10, alignItems: 'center', borderTop: '1px solid var(--border)', paddingTop: 16 }}>
          <button className={`btn ${isDone ? '' : 'btn-primary'}`} onClick={toggleComplete}>
            {isDone ? '✓ Completed — mark incomplete' : 'Mark complete'}
          </button>
          <button className="btn" onClick={() => navigate(`/learn/course/${data.courseId}`)}>Back to course</button>
        </div>
      </div>
    </div>
  );
}

// 2026-10-17 — same header as courses / practices / events / groups. Tile:
// the lesson's own image, else the course's (image, or icon / cover color).
// Background: the lesson's header color, else the course's cover color.
function LessonHero({ lesson }) {
  if (!lesson) return null;
  // Own image → own icon color → the course's icon (image or icon color).
  const img = lesson.coverImageUrl || (lesson.iconColor ? null : lesson.courseCoverImageUrl);
  const tile = lesson.coverImageUrl ? null : (lesson.iconColor || lesson.courseIconColor || null);
  return (
    <ProgramHero kind="Lesson" name={lesson.title || ''}
      iconSrc={img ? api.iconUrl(img) : null}
      coverColor={lesson.headerColor || lesson.courseCoverColor}
      iconColor={tile}
      sub={(lesson.subtitle || lesson.courseTitle) ? (
        <>
          {lesson.subtitle && <div>{lesson.subtitle}</div>}
          {lesson.courseTitle && <div>in <b>{lesson.courseTitle}</b></div>}
        </>
      ) : null} />
  );
}

// Read-only render of a lesson's hero + content blocks, as it appears in the
// reader. Used by the publisher's Preview modal (no notes / progress / Ask
// panels — just the content the way a learner sees it).
export function LessonPreviewBody({ lesson }) {
  const blocks = lesson?.blocks || [];
  return (
    <>
      <LessonHero lesson={lesson} />
      <div className="section">
        {blocks.length === 0
          ? <div style={{ color: 'var(--muted)' }}>This lesson has no content yet.</div>
          : blocks.map((b) => (
            <div key={b.id} style={{ marginBottom: 18 }}>
              <LessonBlockView block={b} lessonId={lesson.id} mySubs={[]} onSubmitted={() => {}}
                initialPosition={0} onSavePosition={() => {}} onAddNote={() => {}} />
            </div>
          ))}
      </div>
    </>
  );
}

// Renders a single lesson block by kind. Blocks appear in author order.
function LessonBlockView({ block, initialPosition, onSavePosition, onAddNote, lessonId, mySubs = [], onSubmitted }) {
  switch (block.kind) {
    case 'AUDIO':
      return block.hasMedia
        ? <AudioPlayer block={block} transcript={block.transcript} initialPosition={initialPosition}
            onSavePosition={onSavePosition} onShare={() => alert('Share to group — coming soon.')} onAddNote={onAddNote} />
        : <MissingMedia kind="audio" />;
    case 'VIDEO':
      return block.hasMedia
        ? <VideoPlayer block={block} transcript={block.transcript} />
        : <MissingMedia kind="video" />;
    case 'RICH_TEXT':
      return <RichText html={block.text} />;
    case 'REFLECTION':
      return (
        <ReflectionResponse
          lessonId={lessonId}
          blockId={block.id}
          prompt={block.text}
          mine={mySubs.filter((s) => s.kind === 'REFLECTION' && s.blockId === block.id)}
          onSubmitted={onSubmitted}
        />
      );
    case 'DOC':
      if (!block.hasMedia) return null;
      return isImageBlock(block) ? <ImageBlock block={block} /> : <DocResource block={block} />;
    case 'TYPEFORM':
      return <FormEmbed url={block.text} />;
    case 'YOUTUBE':
      return <YouTubeEmbed url={block.text} />;
    default:
      return null;
  }
}

/**
 * Resolve any form-provider URL to something iframe-able. Typeform bare IDs
 * (~6-10 alphanumeric) get normalized to the canonical Typeform embed URL;
 * anything else that looks like an https URL is passed through so Tally,
 * Google Forms, Jotform, etc. all just work.
 */
function formEmbedUrl(raw) {
  if (!raw) return null;
  const s = raw.trim();
  if (!s) return null;
  // Bare Typeform ID (Typeform IDs are ~6-10 alphanumeric).
  if (/^[A-Za-z0-9]{4,20}$/.test(s)) return `https://form.typeform.com/to/${s}`;
  const tf = s.match(/typeform\.com\/to\/([A-Za-z0-9]+)/i);
  if (tf) return `https://form.typeform.com/to/${tf[1]}`;
  if (/^https?:\/\//i.test(s)) return s;
  return null;
}

function FormEmbed({ url }) {
  const embed = formEmbedUrl(url);
  if (!embed) return <div style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>⚠ Form link not recognized: {url || '(empty)'}</div>;
  return (
    <div style={{ position: 'relative', width: '100%', paddingBottom: '75%', height: 0, borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border)' }}>
      <iframe
        src={embed}
        title="Embedded form"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
        allow="camera; microphone; autoplay; encrypted-media; fullscreen"
      />
    </div>
  );
}

/**
 * Parse a YouTube URL/ID into an embed URL. Accepts:
 *   https://www.youtube.com/watch?v=VIDEO_ID
 *   https://youtu.be/VIDEO_ID
 *   https://www.youtube.com/shorts/VIDEO_ID
 *   bare 11-char video ID
 */
function youtubeEmbedUrl(raw) {
  if (!raw) return null;
  const s = raw.trim();
  if (!s) return null;
  if (/^[A-Za-z0-9_-]{11}$/.test(s)) return `https://www.youtube.com/embed/${s}`;
  let m = s.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/|v\/))([A-Za-z0-9_-]{11})/);
  if (m) return `https://www.youtube.com/embed/${m[1]}`;
  return null;
}

function YouTubeEmbed({ url }) {
  const embed = youtubeEmbedUrl(url);
  if (!embed) return <div style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>⚠ YouTube link not recognized: {url || '(empty)'}</div>;
  return (
    <div style={{ position: 'relative', width: '100%', paddingBottom: '56.25%', height: 0, borderRadius: 8, overflow: 'hidden', background: '#000' }}>
      <iframe
        src={embed}
        title="YouTube video"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
        allowFullScreen
      />
    </div>
  );
}

// A DOC block whose upload is an image (png/jpg/webp/gif) — rendered inline
// rather than as a download-only "document".
function isImageBlock(block) {
  const ct = block.mediaContentType || '';
  const name = block.mediaFilename || '';
  return /^image\//i.test(ct) || /\.(png|jpe?g|webp|gif)$/i.test(name);
}

// Auto-generated (Whisper) transcript, shown collapsible under its media block.
function TranscriptBlock({ text }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ marginTop: 14, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
      <div className="rlabel" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={() => setOpen((o) => !o)}>
        <span>📄 Transcript</span><span style={{ fontSize: '0.75rem' }}>{open ? 'Hide ▾' : 'Show ▸'}</span>
      </div>
      {open && <div className="transcript-line" style={{ whiteSpace: 'pre-wrap', marginTop: 8 }}>{text}</div>}
    </div>
  );
}

// Lightweight markdown rendering for AI answers: paragraphs, bullet lists, **bold**.
function inlineMd(s) {
  return s.split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
    /^\*\*[^*]+\*\*$/.test(p) ? <strong key={i}>{p.slice(2, -2)}</strong> : p);
}
function AnswerText({ text }) {
  const blocks = (text || '').trim().split(/\n{2,}/);
  return (
    <div style={{ lineHeight: 1.55 }}>
      {blocks.map((blk, bi) => {
        const lines = blk.split('\n').filter((l) => l.trim() !== '');
        const isList = lines.length > 0 && lines.every((l) => /^\s*[-*]\s+/.test(l));
        if (isList) {
          return (
            <ul key={bi} style={{ margin: '6px 0', paddingLeft: 20 }}>
              {lines.map((l, li) => <li key={li}>{inlineMd(l.replace(/^\s*[-*]\s+/, ''))}</li>)}
            </ul>
          );
        }
        return <p key={bi} style={{ margin: '6px 0' }}>{inlineMd(blk)}</p>;
      })}
    </div>
  );
}

// A consistent titled card used for the Ask/Notes panels in the player.
function PlayerPanel({ title, children, panelRef }) {
  return (
    <div className="player-panel" ref={panelRef}>
      <div className="player-panel-head">{title}</div>
      <div className="player-panel-body">{children}</div>
    </div>
  );
}

// Ask questions answered from this lesson's indexed content (B4.4). Keeps a
// running thread, persisted per-lesson on this device so it survives refresh.
function AskThisLesson({ lessonId }) {
  const [q, setQ] = useState('');
  const [asking, setAsking] = useState(false);
  const [thread, setThread] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`cp_ask_${lessonId}`)) || []; } catch { return []; }
  });

  useEffect(() => {
    const done = thread.filter((t) => !t.pending);
    try { localStorage.setItem(`cp_ask_${lessonId}`, JSON.stringify(done)); } catch { /* ignore quota */ }
  }, [thread, lessonId]);

  const ask = async () => {
    const question = q.trim();
    if (!question || asking) return;
    setAsking(true);
    setThread((t) => [...t, { q: question, pending: true }]);
    setQ('');
    try {
      const r = await api.learnAskLesson(lessonId, question);
      setThread((t) => t.map((turn) => (turn.pending ? { q: question, answer: r.answer, citations: r.citations } : turn)));
    } catch (e) {
      const msg = e.status === 429 ? 'Too many questions — try again shortly.' : (e.message || 'Could not get an answer.');
      setThread((t) => t.map((turn) => (turn.pending ? { q: question, error: msg } : turn)));
    } finally {
      setAsking(false);
    }
  };

  return (
    <PlayerPanel title="✨ Ask AI about this lesson">
      {thread.map((turn, i) => <AskTurn key={i} turn={turn} defaultOpen={i === thread.length - 1} />)}

      <div style={{ display: 'flex', gap: 8 }}>
        <input
          className="input"
          style={{ flex: 1 }}
          value={q}
          placeholder="e.g. What are the key takeaways?"
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') ask(); }}
        />
        <button className="btn btn-primary" disabled={asking || !q.trim()} onClick={ask}>{asking ? 'Asking…' : 'Ask'}</button>
      </div>
      <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: 8 }}>Answers are AI-generated from this lesson's indexed content.</div>
    </PlayerPanel>
  );
}

// One collapsible question + answer in the Ask-AI thread.
function AskTurn({ turn, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  const sources = turn.citations ? [...new Set(turn.citations.map((c) => c.filename))] : [];
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', marginBottom: 10, overflow: 'hidden' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{ width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: 'var(--card-soft)', border: 'none', cursor: 'pointer', font: 'inherit', fontWeight: 600 }}
      >
        <span style={{ flex: 1 }}>{turn.q}</span>
        <span style={{ color: 'var(--muted)', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>{open ? 'Hide ▾' : 'Show ▸'}</span>
      </button>
      {open && (
        <div style={{ padding: 12 }}>
          {turn.pending ? <div style={{ color: 'var(--muted)', fontSize: '0.8125rem' }}>Thinking…</div>
            : turn.error ? <div style={{ color: '#b91c1c', fontSize: '0.8125rem' }}>{turn.error}</div>
            : (
              <>
                <AnswerText text={turn.answer} />
                {sources.length > 0 && (
                  <div style={{ marginTop: 6, fontSize: '0.75rem', color: 'var(--muted)' }}>Sources: {sources.join(', ')}</div>
                )}
              </>
            )}
        </div>
      )}
    </div>
  );
}

// True when rich-text HTML has no actual text content.
const htmlEmpty = (h) => !h || !h.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();

// A reflection prompt + the learner's rich-text response, and their prior
// submissions (each its own reply thread).
// `send` (optional) posts the submission — practices pass their own (design/033).
export function ReflectionResponse({ lessonId, blockId, prompt, mine, onSubmitted, send }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const post = send || ((payload) => api.learnSubmit(lessonId, payload));
  const submit = async () => {
    if (htmlEmpty(text) || busy) return;
    setBusy(true);
    try { await post({ blockId, kind: 'REFLECTION', body: text }); setText(''); onSubmitted?.(); }
    catch (e) { alert(e.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="block-reflection">
      <div className="rlabel">📝 Reflection</div>
      <div className="rtext"><RichText html={prompt} /></div>
      <div style={{ marginTop: 10 }}>
        <RichTextEditor value={text} onChange={setText} minHeight={90} placeholder="Write your reflection…" />
      </div>
      <div><button className="btn btn-primary" style={{ marginTop: 10 }} disabled={busy || htmlEmpty(text)} onClick={submit}>{busy ? 'Submitting…' : 'Submit reflection'}</button></div>
      <PriorSubmissions items={mine} label="Your reflections" onReplied={onSubmitted} />
    </div>
  );
}

// Free-form question to the course publisher (distinct from the AI ask).
export function AskInstructor({ lessonId, mine, onSubmitted, send }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const post = send || ((payload) => api.learnSubmit(lessonId, payload));
  const submit = async () => {
    if (htmlEmpty(text) || busy) return;
    setBusy(true);
    try { await post({ kind: 'QUESTION', body: text }); setText(''); onSubmitted?.(); }
    catch (e) { alert(e.message); }
    finally { setBusy(false); }
  };
  return (
    <PlayerPanel title="✉️ Ask the instructor">
      <RichTextEditor value={text} onChange={setText} minHeight={80} placeholder="Send a question to the instructor…" />
      <div><button className="btn btn-primary" style={{ marginTop: 10 }} disabled={busy || htmlEmpty(text)} onClick={submit}>{busy ? 'Sending…' : 'Send question'}</button></div>
      <PriorSubmissions items={mine} label="Your questions" onReplied={onSubmitted} />
    </PlayerPanel>
  );
}

function PriorSubmissions({ items, label, onReplied }) {
  if (!items || items.length === 0) return null;
  return (
    <div style={{ marginTop: 14 }}>
      <div className="sub-label">{label}</div>
      {items.map((s) => <SubmissionItem key={s.id} submission={s} onChanged={onReplied} />)}
    </div>
  );
}

// The learner's own submission: editable body, the publisher conversation
// thread, and (once the publisher has replied) a reply box to continue it.
function SubmissionItem({ submission, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState(submission.body);
  const [savingEdit, setSavingEdit] = useState(false);
  const [reply, setReply] = useState('');
  const [replying, setReplying] = useState(false);
  const [replyOpen, setReplyOpen] = useState(false);
  const replies = submission.replies || [];

  const saveEdit = async () => {
    if (htmlEmpty(body) || savingEdit) return;
    setSavingEdit(true);
    try { await api.learnEditSubmission(submission.id, body); setEditing(false); onChanged?.(); }
    catch (e) { alert(e.message); }
    finally { setSavingEdit(false); }
  };
  const sendReply = async () => {
    if (htmlEmpty(reply) || replying) return;
    setReplying(true);
    try { await api.learnReplySubmission(submission.id, reply); setReply(''); setReplyOpen(false); onChanged?.(); }
    catch (e) { alert(e.message); }
    finally { setReplying(false); }
  };

  return (
    <div className="submission-card">
      {editing ? (
        <>
          <RichTextEditor value={body} onChange={setBody} minHeight={80} />
          <div style={{ marginTop: 6, display: 'flex', gap: 6 }}>
            <button className="btn btn-sm btn-primary" disabled={savingEdit || htmlEmpty(body)} onClick={saveEdit}>{savingEdit ? 'Saving…' : 'Save'}</button>
            <button className="btn btn-sm" onClick={() => { setBody(submission.body); setEditing(false); }}>Cancel</button>
          </div>
        </>
      ) : (
        <>
          <RichText html={submission.body} />
          <div className="submission-meta">
            <span>{new Date(submission.createdAt).toLocaleString()}</span>
            <button className="btn btn-sm" style={{ marginLeft: 8 }} onClick={() => { setBody(submission.body); setEditing(true); }}>Edit</button>
          </div>
        </>
      )}

      {replies.length > 0 && (
        <div className="reply-thread">
          {replies.map((r) => (
            <div key={r.id} className={`reply-bubble${r.byInstructor ? ' from-instructor' : ''}`}>
              <div className="reply-who">{r.byInstructor ? `🎓 ${r.authorName || 'Instructor'}` : 'You'} · {new Date(r.createdAt).toLocaleString()}</div>
              <RichText html={r.body} />
            </div>
          ))}
          {replyOpen ? (
            <div>
              <RichTextEditor value={reply} onChange={setReply} minHeight={56} placeholder="Reply to the conversation…" />
              <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                <button className="btn btn-primary" disabled={replying || htmlEmpty(reply)} onClick={sendReply}>{replying ? 'Sending…' : 'Send reply'}</button>
                <button className="btn" onClick={() => { setReply(''); setReplyOpen(false); }}>Cancel</button>
              </div>
            </div>
          ) : (
            <div><button className="btn btn-primary" onClick={() => setReplyOpen(true)}>↩ Reply</button></div>
          )}
        </div>
      )}
    </div>
  );
}

function MissingMedia({ kind }) {
  return (
    <div className="player-controls">
      <div className="block-media-missing">
        This lesson has a {kind} block but no media is attached yet. Open the lesson in the
        editor and (re)upload the file.
      </div>
    </div>
  );
}

// Resolve a playable src for a media block. Prefers a signed GCS URL (direct
// fetch, range/seeking, no gw buffering); falls back to a blob object URL from
// the byte-stream endpoint when the store can't sign (local dev).
function useMediaSrc(blockId, enabled) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    if (!enabled) return undefined;
    let obj; let cancelled = false;
    (async () => {
      try {
        const r = await api.learnBlockMediaUrl(blockId);
        if (r && r.url) { if (!cancelled) setUrl(r.url); return; }
        // url === null → local store, use the byte stream.
        const o = await api.blockMediaObjectUrl(blockId);
        if (cancelled) { URL.revokeObjectURL(o); return; }
        obj = o; setUrl(o);
      } catch (e) {
        // 404 = media gone (stale upload); anything else → try the byte stream once.
        if (e?.status === 404) { if (!cancelled) setErr(true); return; }
        try {
          const o = await api.blockMediaObjectUrl(blockId);
          if (cancelled) { URL.revokeObjectURL(o); return; }
          obj = o; setUrl(o);
        } catch { if (!cancelled) setErr(true); }
      }
    })();
    return () => { cancelled = true; if (obj) URL.revokeObjectURL(obj); };
  }, [blockId, enabled]);
  return { url, err };
}

function AudioPlayer({ block, transcript, initialPosition, onSavePosition, onShare, onAddNote }) {
  const ref = useRef(null);
  const { url, err } = useMediaSrc(block.id, true);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);
  const [rate, setRate] = useState(1);

  const toggle = () => { const a = ref.current; if (!a) return; a.paused ? a.play() : a.pause(); };
  const skip = (d) => { const a = ref.current; if (a) a.currentTime = Math.max(0, Math.min(a.duration || 0, a.currentTime + d)); };
  const cycleRate = () => { const next = RATES[(RATES.indexOf(rate) + 1) % RATES.length]; setRate(next); if (ref.current) ref.current.playbackRate = next; };
  const seek = (e) => { const r = e.currentTarget.getBoundingClientRect(); const pct = (e.clientX - r.left) / r.width; if (ref.current && dur) ref.current.currentTime = pct * dur; };

  if (err) return <div className="player-controls"><div className="block-media-missing">Couldn’t load audio.</div></div>;

  return (
    <div className="player-controls">
      <div className="media-head"><span>🎧 {block.mediaFilename || 'Audio'}</span></div>
      <audio
        ref={ref}
        src={url || undefined}
        onLoadedMetadata={(e) => { setDur(e.target.duration); if (initialPosition) e.target.currentTime = Math.min(initialPosition, (e.target.duration || 1) - 1); e.target.playbackRate = rate; }}
        onTimeUpdate={(e) => setCur(e.target.currentTime)}
        onPlay={() => setPlaying(true)}
        onPause={() => { setPlaying(false); onSavePosition?.(Math.floor(ref.current?.currentTime || 0)); }}
        onEnded={() => onSavePosition?.(Math.floor(dur))}
      />
      <div className="progress" onClick={seek}><div className="fill" style={{ width: dur ? `${(cur / dur) * 100}%` : '0%' }} /></div>
      <div className="progress-times"><span>{fmt(cur)}</span><span>-{fmt(Math.max(0, dur - cur))}</span></div>
      <div className="controls-row">
        <button className="ctrl" onClick={() => skip(-15)} title="Back 15s">15⏪</button>
        <button className="ctrl play" onClick={toggle}>{playing ? '⏸' : '▶'}</button>
        <button className="ctrl" onClick={() => skip(30)} title="Forward 30s">⏩30</button>
      </div>
      <div className="player-actions">
        {url && <a className="btn" href={url} download={block.mediaFilename || 'audio'}>↓ Download</a>}
        <button className="btn" onClick={onAddNote}>📝 Add note</button>
        <button className="btn" onClick={cycleRate}>⏱ {rate}x</button>
        <button className="btn" onClick={onShare}>📤 Share to group</button>
      </div>
      {transcript && <TranscriptBlock text={transcript} />}
    </div>
  );
}

function VideoPlayer({ block, transcript }) {
  // Transcoded video plays via hls.js against the signed-manifest endpoint;
  // un-transcoded (or dev) video falls back to the progressive object URL.
  if (block.hls) return <HlsVideo block={block} transcript={transcript} />;
  return <ProgressiveVideo block={block} transcript={transcript} />;
}

function ProgressiveVideo({ block, transcript }) {
  const { url, err } = useMediaSrc(block.id, true);
  if (err) return <div className="player-controls"><div className="block-media-missing">Couldn’t load video.</div>{transcript && <TranscriptBlock text={transcript} />}</div>;
  if (!url) return <div className="player-controls"><div className="block-media-missing">Loading video…</div></div>;
  return <div className="player-controls"><div className="media-head"><span>🎬 {block.mediaFilename || 'Video'}</span></div><video controls src={url} />{transcript && <TranscriptBlock text={transcript} />}</div>;
}

function HlsVideo({ block, transcript }) {
  const ref = useRef(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    const video = ref.current;
    if (!video) return undefined;
    let hls; let cancelled = false;
    (async () => {
      const token = await getIdToken();
      if (cancelled) return;
      const src = api.learnHlsUrl(block.id);
      if (Hls.isSupported()) {
        hls = new Hls({
          // Add the bearer only for the gw manifest request; segment requests
          // go to GCS as signed URLs and must not carry an Authorization header.
          xhrSetup: (xhr, url) => {
            if (token && url.startsWith(api.baseUrl)) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
          },
        });
        hls.on(Hls.Events.ERROR, (_e, data) => { if (data.fatal) setErr(true); });
        hls.loadSource(src);
        hls.attachMedia(video);
      } else {
        // Targeted desktop browsers all support MSE/hls.js; native-only HLS
        // (older iOS Safari) can't send the bearer, so we surface a notice.
        setErr(true);
      }
    })();
    return () => { cancelled = true; if (hls) hls.destroy(); };
  }, [block.id]);
  if (err) return <div className="player-controls"><div className="block-media-missing">Couldn’t load video.</div>{transcript && <TranscriptBlock text={transcript} />}</div>;
  return <div className="player-controls"><div className="media-head"><span>🎬 {block.mediaFilename || 'Video'}</span></div><video ref={ref} controls />{transcript && <TranscriptBlock text={transcript} />}</div>;
}

function ImageBlock({ block }) {
  const { url, err } = useMediaSrc(block.id, true);
  if (err) return <div className="block-media-missing">This image is no longer available — please re-upload it in the lesson editor.</div>;
  if (!url) return <div style={{ fontSize: '0.8125rem', color: 'var(--muted)' }}>Loading…</div>;
  // Just the image — no filename/download header.
  return <img src={url} alt={block.mediaFilename || ''} style={{ maxWidth: '100%', borderRadius: 8, display: 'block' }} />;
}

function DocResource({ block }) {
  const { url, err } = useMediaSrc(block.id, true);
  const name = block.mediaFilename || '';
  const ct = block.mediaContentType || '';
  const isPdf = /pdf/i.test(ct) || /\.pdf$/i.test(name);
  const isDocx = /word|officedocument\.wordprocessing/i.test(ct) || /\.docx?$/i.test(name);
  return (
    <div className="player-controls">
      <div className="media-head">
        <span>📄 {block.mediaFilename || 'Document'}</span>
        {url && <a className="btn btn-sm" href={url} download={block.mediaFilename || 'document'}>Download</a>}
      </div>
      {err
        ? <div className="block-media-missing" style={{ marginBottom: 0 }}>This file is no longer available — please re-upload it in the lesson editor.</div>
        : !url
          ? <div style={{ fontSize: '0.8125rem', color: 'var(--muted)' }}>Loading…</div>
          : (
            <>
              {isPdf && (
                <iframe
                  title={block.mediaFilename || 'document'}
                  /* #pagemode=none collapses the thumbnails/bookmarks pane by default. */
                  src={`${url}#pagemode=none`}
                  style={{ width: '100%', height: 600, border: '1px solid var(--border)', borderRadius: 8 }}
                />
              )}
              {isDocx && <DocxPreview url={url} />}
            </>
          )}
    </div>
  );
}

// Render a .docx inline by converting it to HTML in the browser with mammoth.
// Fetches the same (signed or blob) URL the Download button uses — so it works
// for GCS files without buffering through gw, and never hits a stale file:// blob.
// Not pixel-perfect Word layout — a semantic preview (headings, lists, tables).
function DocxPreview({ url }) {
  const [html, setHtml] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(url);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const arrayBuffer = await resp.arrayBuffer();
        const mod = await import('mammoth/mammoth.browser');
        const mammoth = mod.default || mod;
        const { value } = await mammoth.convertToHtml({ arrayBuffer });
        if (!cancelled) setHtml(value || '<p><em>(empty document)</em></p>');
      } catch { if (!cancelled) setErr(true); }
    })();
    return () => { cancelled = true; };
  }, [url]);

  if (err) return <div style={{ fontSize: '0.78125rem', color: 'var(--muted)', marginTop: 8 }}>Couldn’t render a preview — use Download to open the file.</div>;
  if (html == null) return <div style={{ fontSize: '0.78125rem', color: 'var(--muted)', marginTop: 8 }}>Rendering preview…</div>;
  return (
    <div
      className="docx-preview"
      style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 18, marginTop: 8, background: '#fff', maxHeight: 600, overflow: 'auto' }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
